import React from "react"
import { Link } from "gatsby"
import styled, { css } from "styled-components"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
// import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import ArticleAd from "./contentful-components/article-ad"
import ArticleCallout from "./contentful-components/article-callout"
import ArticleDefinition from "./contentful-components/article-definition"
import ArticleExpertTip from "./contentful-components/article-expert-tip"
import ArticleQuote from "./contentful-components/article-quote"
import ArticleToolTip from "./contentful-components/article-tooltip"
import ArticleImage from "./contentful-components/article-image"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const above = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const ArticleWrapper = styled.div`
  *:first-child {
    margin-top: 0px !important;
  }

  /* Article Component styles */

  .article-component {
    margin-top: 48px;
  }

  .article-component + .article-component {
    margin-top: 56px;
  }

  img + .article-component {
    ${above.mobile`
    margin-top:80px;
    `}
  }

  /* Body styles */
  p {
    font-size: 20px;
    line-height: 32px;
    margin: 32px 0 0;

    strong {
      font-weight: 600;
    }
  }

  h2 + p {
    margin-top: 16px;
    ${above.mobile`
    margin-top:20px;
    `}
  }

  h3 + p {
    margin-top: 12px;
    ${above.mobile`
    margin-top:16px;
    `}
  }

  h4 + p {
    margin-top: 8px;
    ${above.mobile`
    margin-top:12px;
    `}
  }

  h5 + p {
    margin-top: 4px;
    ${above.mobile`
    margin-top:8px;
    `}
  }

  img + p {
    margin-top: 80px;
  }

  .article-image + p {
    margin-top: 80px !important;
  }

  .article-component + p {
    margin-top: 48px;
  }

  .article-component + .article-component + p {
    margin-top: 56px;
  }
  /* h2 styles */
  h2 {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 36px;
    line-height: 44px;
    margin: 48px 0 0;

    ${above.mobile`
    font-size:44px;
    line-height:60px;
    margin:56px 0 0;
    `}
  }

  img + h2 {
    margin-top: 80px;
  }
  .article-component + h2 {
    margin-top: 56px;
    ${above.mobile`
    margin-top:48px;
    `}
  }

  .article-component + .article-component + h2 {
    margin-top: 56px;
  }
  /* h3 styles */
  h3 {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 28px;
    line-height: 36px;
    margin: 40px 0 0;
    ${above.mobile`
    font-size:36px;
    line-height:44px;
    margin:48px 0 0;
    `}
  }

  h2 + h3 {
    margin-top: 24px;
    ${above.mobile`
    margin-top:32px;
    `}
  }

  img + h3 {
    margin-top: 80px;
  }

  .article-component + h3 {
    margin-top: 48px;
  }

  .article-component + .article-component + h3 {
    margin-top: 56px;
  }
  /* h4 styles */

  h4 {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 36px;
    margin: 32px 0 0;
    ${above.mobile`
    font-size:28px;
    margin:40px 0 0;

    `}
  }

  h2 + h4 {
    margin-top: 0;
  }

  h3 + h4 {
    margin-top: 16px;
    ${above.mobile`
    margin-top:24px;
    `}
  }

  img + h4 {
    margin-top: 80px;
  }

  .article-component + h4 {
    margin-top: 48px;
  }

  .article-component + .article-component + h4 {
    margin-top: 56px;
  }

  /* h5 styles */

  h5 {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 32px;
    margin: 24px 0 0;
    ${above.mobile`
    font-size:22px;
    line-height:28px;
    margin:32px 0 0;
    `}
  }

  h4 + h5 {
    margin-top: 8px;
    ${above.mobile`
    margin-top:16px;
    `}
  }

  h5 + h5 {
    margin-top: 0;
  }

  img + h5 {
    margin-top: 80px;
  }

  .article-component + h5 {
    margin-top: 48px;
  }

  .article-component + .article-component + h5 {
    margin-top: 56px;
  }

  /* a tag styles */

  a {
    color: var(--pacific-blue);
    :hover {
      text-decoration: underline;
    }
  }

  /* img styles */
  img {
    margin: 48px 0 0;
    ${above.mobile`
    margin-top:64px;
    `}
    ${above.tablet`
    margin-top:80px;
    `}
  }

  /* ordered list styles */
  ol {
    margin-left: 16px;
    margin-top: 16px;
    padding-left: 32px;
    ${above.mobile`
    margin-left:32px;
    `}
    ${below.mobile`padding-left: 8px;`}
    li {
      list-style: decimal !important;
      font-size: 20px;
      line-height: 32px;
      ${below.mobile`padding-left: 24px;`}
      :before {
        display: none;
      }
    }
  }

  /* unordered list styles */
  ul {
    margin-left: 16px;
    margin-top: 16px;
    ${above.mobile`
    margin-left:32px;
    `}

    li {
      font-size: 24px;
      line-height: 32px;
      margin-top: 8px;
      ${below.mobile`padding-left: 24px;`}

      p {
        font-size: 20px;
        line-height: 28px;
        margin-top: 8px;
      }
      :before {
        position: absolute;
        top: 16px;
        left: 6px;
        content: "";
        background-color: black;
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }
    }
    li > li {
      margin-top: 4px;
    }
  }
  /* blockquote styles */
  blockquote {
    margin: 40px 0;
    padding: 0;
    p {
      font-size: 32px;
      line-height: 40px;
      color: #4082c4;
      font-style: italic;
      ${below.mobile`
      font-size: 28px;
      line-height: 32px;
      `}
    }
  }
`

const ArticleBody = props => {
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: node => {
        return (
          <img src={node.data.target.file.url} alt={node.data.target.title} />
        )
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        if (node.data.target.__typename === "ContentfulArticleCallout") {
          return (
            <ArticleCallout
              category={props.articleData.category}
              fields={node.data.target}
              text={JSON.parse(node.data.target.callout.raw)}
              bankerData={props.bankerData}
            />
          )
        }

        if (node.data.target.__typename === "ContentfulArticleDefinition") {
          return (
            <ArticleDefinition
              category={props.articleData.category}
              fields={node.data.target}
              bankerData={props.bankerData}
            />
          )
        }

        if (node.data.target.__typename === "ContentfulArticleTip") {
          return (
            <ArticleExpertTip
              category={props.articleData.category}
              fields={node.data.target}
              bankerData={props.bankerData}
            />
          )
        }
        if (node.data.target.__typename === "ContentfulArticleQuote") {
          return (
            <ArticleQuote
              category={props.articleData.category}
              fields={node.data.target}
              bankerData={props.bankerData}
            />
          )
        }

        if (node.data.target.__typename === "ContentfulArticleImage") {
          return (
            <ArticleImage
              category={props.articleData.category}
              fields={node.data.target}
              bankerData={props.bankerData}
            />
          )
        }

        if (node.data.target.__typename === "ContentfulArticleAd") {
          return <ArticleAd type={node.data.target.type} />
        } else {
          return <p>This is another type of embedded entry</p>
        }
      },
      [INLINES.EMBEDDED_ENTRY]: node => {
        if (node.data.target.__typename === "ContentfulArticleTooltip") {
          return (
            <ArticleToolTip
              category={props.articleData.category}
              fields={node.data.target}
              setTooltipOverlay={props.setTooltipOverlay}
            />
          )
        } else if (
          node.data.target.__typename === "ContentfulArticleInternalLink"
        ) {
          let slug
          // If internal link is to a KC article
          if (
            !node.data.target.category ||
            node.data.target.category === "KC"
          ) {
            slug = props.bankerData
              ? `/${props.bankerData?.slug}/knowledge-center/${node.data.target.link}`
              : `/knowledge-center/${node.data.target.link}`
          }
          // If internal link is not to a KC article
          else {
            slug = `/${node.data.target.link}`
          }

          return <Link to={slug}>{node.data.target.linkText}</Link>
        }
      },
      [INLINES.HYPERLINK]: node => {
        return (
          <a href={node.data.uri} target="_blank" rel="noreferrer">
            {node.content[0].value}
          </a>
        )
      },
    },
  }
  return (
    <ArticleWrapper>
      {/* {documentToReactComponents(JSON.stringify(props.richText), {
        renderNode: {
          [BLOCKS.EMBEDDED_ASSET]: node => {
            return (
              <img
                src={node.data.target.fields.file["en-US"].url}
                alt={node.data.target.fields.title["en-US"]}
              />
            )
          },
          [BLOCKS.EMBEDDED_ENTRY]: node => {
            if (node.data.target.sys.contentType.sys.id === "articleCallout") {
              return (
                <ArticleCallout
                  category={props.articleData.category}
                  text={node.data.target.fields.callout["en-US"]}
                />
              )
            }

            if (
              node.data.target.sys.contentType.sys.id === "articleDefinition"
            ) {
              return (
                <ArticleDefinition
                  category={props.articleData.category}
                  fields={node.data.target.fields}
                />
              )
            }

            if (node.data.target.sys.contentType.sys.id === "articleTip") {
              return (
                <ArticleExpertTip
                  category={props.articleData.category}
                  fields={node.data.target.fields}
                />
              )
            }
            if (node.data.target.sys.contentType.sys.id === "articleQuote") {
              return (
                <ArticleQuote
                  category={props.articleData.category}
                  fields={node.data.target.fields}
                />
              )
            }

            if (node.data.target.sys.contentType.sys.id === "articleImage") {
              return (
                <ArticleImage
                  category={props.articleData.category}
                  fields={node.data.target.fields}
                />
              )
            }

            if (node.data.target.sys.contentType.sys.id === "articleAd") {
              return <ArticleAd type={node.data.target.fields.type["en-US"]} />
            } else {
              return <p>This is another type of embedded entry</p>
            }
          },
          [INLINES.EMBEDDED_ENTRY]: node => {
            if (node.data.target.sys.contentType.sys.id === "articleTooltip") {
              return (
                <ArticleToolTip
                  category={props.articleData.category}
                  fields={node.data.target.fields}
                  setTooltipOverlay={props.setTooltipOverlay}
                />
              )
            } else if (
              node.data.target.sys.contentType.sys.id === "ctaInternalLink"
            ) {
              let slug
              // If internal link is to a KC article
              if (
                !node.data.target.fields.category ||
                node.data.target.fields.category["en-US"] === "KC"
              ) {
                slug =
                  props.bankerData
                    ? `/mb/knowledge-center/${node.data.target.fields.link["en-US"]}?mb=${props.bankerData?.slug}`
                    : `/knowledge-center/${node.data.target.fields.link["en-US"]}`
              }
              // If internal link is not to a KC article
              else {
                slug = `/${node.data.target.fields.link["en-US"]}`
              }

              return (
                <Link to={slug}>
                  {node.data.target.fields.linkText["en-US"]}
                </Link>
              )
            }
          },
          [INLINES.HYPERLINK]: node => {
            return (
              <a href={node.data.uri} target="_blank" rel="noreferrer">
                {node.content[0].value}
              </a>
            )
          },
        },
      })} */}
      {renderRichText(props.richText, options)}
    </ArticleWrapper>
  )
}

export default ArticleBody
